/**
 * Serialization module for Langton's Ant Gallery
 * Requirements: 4.5, 4.6
 * 
 * Provides RLE encoding/decoding for grid state and full simulation state serialization
 */

import type { SimulationState, Direction } from './types.js';

/**
 * SerializedState - сериализованное состояние симуляции
 * Используется для передачи по WebSocket
 */
export interface SerializedState {
  grid: string;           // RLE-encoded grid
  antX: number;
  antY: number;
  antDir: Direction;
  stepCount: number;
}

/**
 * Encode a 2D grid using Run-Length Encoding (RLE)
 * Format: "value:count,value:count,..." with rows separated by ";"
 * 
 * @param grid - 2D array of color indices
 * @returns RLE-encoded string
 */
export function encodeGrid(grid: number[][]): string {
  if (grid.length === 0) return '';
  
  const encodedRows: string[] = [];
  
  for (const row of grid) {
    if (row.length === 0) {
      encodedRows.push('');
      continue;
    }
    
    const runs: string[] = [];
    let currentValue = row[0];
    let count = 1;
    
    for (let i = 1; i < row.length; i++) {
      if (row[i] === currentValue) {
        count++;
      } else {
        runs.push(`${currentValue}:${count}`);
        currentValue = row[i];
        count = 1;
      }
    }
    // Push the last run
    runs.push(`${currentValue}:${count}`);
    
    encodedRows.push(runs.join(','));
  }
  
  return encodedRows.join(';');
}

/**
 * Decode an RLE-encoded string back to a 2D grid
 * 
 * @param encoded - RLE-encoded string
 * @returns 2D array of color indices
 */
export function decodeGrid(encoded: string): number[][] {
  if (encoded === '') return [];
  
  const rows = encoded.split(';');
  const grid: number[][] = [];
  
  for (const rowStr of rows) {
    if (rowStr === '') {
      grid.push([]);
      continue;
    }
    
    const row: number[] = [];
    const runs = rowStr.split(',');
    
    for (const run of runs) {
      const [valueStr, countStr] = run.split(':');
      const value = parseInt(valueStr, 10);
      const count = parseInt(countStr, 10);
      
      for (let i = 0; i < count; i++) {
        row.push(value);
      }
    }
    
    grid.push(row);
  }
  
  return grid;
}

/**
 * Serialize a full simulation state
 * 
 * @param state - SimulationState to serialize
 * @returns SerializedState object
 */
export function serializeState(state: SimulationState): SerializedState {
  return {
    grid: encodeGrid(state.grid),
    antX: state.ant.position.x,
    antY: state.ant.position.y,
    antDir: state.ant.direction,
    stepCount: state.stepCount
  };
}

/**
 * Deserialize a SerializedState back to SimulationState
 * 
 * @param serialized - SerializedState to deserialize
 * @returns SimulationState object
 */
export function deserializeState(serialized: SerializedState): SimulationState {
  return {
    grid: decodeGrid(serialized.grid),
    ant: {
      position: {
        x: serialized.antX,
        y: serialized.antY
      },
      direction: serialized.antDir
    },
    stepCount: serialized.stepCount
  };
}
